import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Dialog, DialogTitle, Select, MenuItem, TextField, Button, DialogContent, DialogActions,
} from '@material-ui/core';
import {
  frequencyIds, periods, frequencies, selectors, buildEmailReport,
} from '@smorder/logic';
import {
  EmailListInput, ReportFrequencyInput,
} from '@smorder/core';
import validateEmail from '../../util/validate-email';

import {
  EmailReportsTable, ListSelector,
} from '..';

import ReportsFilter from '../../main/filters/reports-filter/reports-filter';
import ParamFilter from '../../main/filters/param-filter/param-filter';
import LoadingIndicator from '../loading-indicator/loading-indicator';
import { daysOfWeek } from '../../models';

const styles = {
  block: {
    paddingBottom: 15,
  },
  frequencySelect: {
    marginRight: 10,
  },
  header: {
    borderBottom: '1px solid #CCCCCC',
  },
  inlineTextfield: {
    marginLeft: 10,
    marginRight: 10,
    width: '50px',
  },
  submitButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  paramFilters: {
    padding: 0,
    paddingBottom: '15px',
    flex: 'auto',
    borderBottom: '1px solid #CCCCCC',
  },
};

const defaultState = {
  LocationId: '',
  ReportId: '',
  frequency: frequencyIds.daily,
  interval: 0,
  date: null,
  sendoutTime: '',
  period: periods[0].id,
  EmailTo: [],
  StartOffset: -periods[0].duration,
  scheduleId: null,
  offsetUnit: periods[0].id,
  hasParameters: false,
};

class EmailReportsDialog extends Component {
  constructor(props) {
    super(props);

    this.state = defaultState;
  }

  componentDidUpdate(prevProps) {
    const {
      open, selectedLocation, selectedReport, loadEmailReports,
    } = this.props;
    const { open: prevOpen } = prevProps;

    if (!prevOpen && open && selectedLocation && selectedReport) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        LocationId: selectedLocation.id,
        ReportId: selectedReport.reportID,
        hasParameters: selectedReport.reportItems[0].hasParameters,
      });
      loadEmailReports(selectedLocation && selectedLocation.id);
    }
  }

  get parameters() {
    const { parameters } = this.props;


    const paramOptions = { };

    parameters.filter(p => p.value).forEach(({ name, value }) => { paramOptions[name] = value; });

    return paramOptions;
  }

  get emailReportState() {
    const { frequency, interval } = this.state;

    // use all days when no day is selected
    if (frequency === frequencyIds.daily && interval === 0) {
      const allDays = daysOfWeek.map(d => d.value).reduce((a, b) => a + b);

      return {
        ...this.state,
        interval: allDays,
      };
    }

    return this.state;
  }

  handleSelectLocation = (locationId) => {
    const { locations, loadEmailReports } = this.props;
    const { ReportId } = this.state;

    loadEmailReports(locationId);

    const selectedLocation = locations.find(l => l.id === locationId);
    const selectedReport = selectedLocation.reports.find(r => r.reportID === ReportId);

    if (selectedReport) {
      this.setState({ LocationId: locationId });
    } else {
      const report = selectedLocation.reports[0];

      this.setState({ LocationId: locationId, ReportId: report.reportID, hasParameters: report.reportItems[0].hasParameters });
    }
  }

  handleCreateEmailReport = () => {
    const { createEmailReport } = this.props;
    const { hasParameters } = this.state;

    const additionalConfig = {
      isPdfReport: hasParameters,
      parameters: hasParameters ? this.parameters : null,
    };

    createEmailReport(buildEmailReport(this.emailReportState, null, additionalConfig));
  }

  handleUpdateEmailReport = () => {
    const { updateEmailReport, emailReports } = this.props;
    const {
      scheduleId, hasParameters,
    } = this.state;

    if (!scheduleId) {
      console.warn('no scheduleId was provided');
      return;
    }

    const selectedEmailReport = emailReports.find(r => r.scheduleId === scheduleId);

    const additionalConfig = {
      isPdfReport: hasParameters,
      parameters: hasParameters ? this.parameters : null,
    };

    updateEmailReport(buildEmailReport(this.emailReportState, selectedEmailReport, additionalConfig));
  }

  handleChangeOffset = (e) => {
    const { offsetUnit } = this.state;

    const offsetPeriod = periods.find(p => p.id === offsetUnit);

    this.setState({
      StartOffset: e.target.value * offsetPeriod.duration,
    });
  }

  handleChangePeriod = (e) => {
    const selectedPeriod = periods.find(p => p.id === e.target.value);

    this.setState({
      period: selectedPeriod.id,
      StartOffset: -selectedPeriod.duration,
      offsetUnit: selectedPeriod.id,
    });
  }

  handleSelectReportForEdit = (scheduleId) => {
    const { emailReports } = this.props;

    const selectedEmailReport = emailReports.find(r => r.scheduleId === scheduleId);

    const { frequency, nextSendout } = selectedEmailReport;
    const date = frequency === frequencyIds.once ? moment(nextSendout) : null;

    const config = JSON.parse(selectedEmailReport.config);


    const period = periods.find(p => p.duration === config.Duration);

    this.setState({
      date,
      period: period ? period.id : null,
      offsetUnit: period ? period.id : defaultState.offsetUnit,
      ...selectedEmailReport,
      ...config,
    }, () => {
      if (config.IsPdfReport) {
        this.handleChangeReport(config.ReportId);
        this.handleSetParameters(config.Parameters);
      }
    });
  };

  handleSetParameters = (parameters) => {
    const { setEmailParams } = this.props;
    if (!parameters) {
      return;
    }

    Object.entries(parameters).forEach(([name, value]) => {
      setEmailParams({ name, value });
    });
  }

  handleCancelEdit = () => {
    const { selectedLocation, selectedReport } = this.props;

    this.setState({
      ...defaultState,
      LocationId: selectedLocation.id,
      ReportId: selectedReport.reportID,
    });
  }

  handleChangeReport = (ReportId) => {
    const { selectedLocation, loadParams } = this.props;
    const { StartOffset } = this.state;

    const report = selectedLocation.reports.find(r => r.reportID === ReportId);
    let hasParameters = false;

    if (report) {
      const [widget] = report.reportItems;

      const { reportWidgetID } = widget;
      // eslint-disable-next-line prefer-destructuring
      hasParameters = widget.hasParameters;

      const end = moment().format();
      const start = moment().add(StartOffset, 'milliseconds');

      if (hasParameters) {
        loadParams({
          locationId: selectedLocation.id,
          widgetId: reportWidgetID,
          datetimeRange: {
            start,
            end,
          },
        });
      }
    }


    this.setState({ ReportId, hasParameters });
  }

  isSubmitAllowed() {
    const {
      EmailTo, sendoutTime, frequency, date,
    } = this.state;

    return EmailTo.length > 0
      && sendoutTime
      && !(frequency === frequencyIds.once && date === null);
  }

  renderFrequencySelect() {
    const { frequency } = this.state;

    const selectedInterval = frequencies.find(i => i.id === frequency);
    return (
      <div style={styles.block}>
        <h3>Intervall</h3>
        <ReportFrequencyInput
          {...this.state}
          frequency={frequency}
          frequencies={frequencies}
          selectors={selectors}
          selectedInterval={selectedInterval}
          onChange={newState => this.setState(newState)}
        />
      </div>
    );
  }

  renderOffsetInput() {
    const { StartOffset, offsetUnit } = this.state;

    const offsetPeriod = periods.find(p => p.id === offsetUnit);

    return (
      <div style={styles.block}>
        <h3>Versatz</h3>
        <div style={{ display: 'flex' }}>
          <TextField
            style={styles.inlineTextfield}
            value={StartOffset / offsetPeriod.duration}
            type="number"
            onChange={this.handleChangeOffset}
          />
          <Select value={offsetUnit} onChange={e => this.setState({ offsetUnit: e.target.value })}>
            {periods.map(p => <MenuItem key={p.id} value={p.id}>{p.offsetUnitName}</MenuItem>)}
          </Select>
        </div>
      </div>
    );
  }

  renderSendoutTimeInput() {
    const { sendoutTime } = this.state;
    return (
      <div style={styles.block}>
        <h3>Aussendezeitpunkt</h3>
        <TextField
          fullWidth
          type="time"
          value={sendoutTime}
          onChange={e => this.setState({ sendoutTime: e.target.value })}
        />
      </div>
    );
  }

  renderPeriodSelect() {
    const { period } = this.state;

    return (
      <div style={styles.block}>
        <h3>Zeitraum</h3>
        <Select fullWidth value={period} onChange={this.handleChangePeriod}>
          {periods.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </Select>
      </div>
    );
  }

  renderEmailTo() {
    const { EmailTo } = this.state;

    return (
      <div style={styles.block}>
        <h3>Empfangs E-Mail Addressen</h3>
        <EmailListInput
          value={EmailTo}
          onChange={e => this.setState({ EmailTo: e })}
          validateEmail={validateEmail}
        />
      </div>
    );
  }

  renderEmailReports() {
    const {
      emailReports, locations, deleteEmailReport,
    } = this.props;
    const { scheduleId } = this.state;

    const reports = locations.map(l => l.reports).flat();

    return (
      <EmailReportsTable
        data={emailReports}
        frequencies={frequencies}
        reports={reports}
        locations={locations}
        periods={periods}
        deleteEmailReport={deleteEmailReport}
        selectEmailReportForEdit={this.handleSelectReportForEdit}
        selectedEmailReport={scheduleId}
      />
    );
  }

  renderParameters() {
    const { hasParameters } = this.state;
    const { parameters, setEmailParams, isLoadingParameters } = this.props;

    if (hasParameters && isLoadingParameters) {
      return <LoadingIndicator />;
    }

    if (!hasParameters || parameters?.length < 1) {
      return null;
    }


    const paramFilters = parameters?.map(param => (
      <ParamFilter key={param.name} param={param} setParamValue={setEmailParams} style={styles.paramFilters} />));

    return (
      <div>
        {paramFilters}
      </div>
    );
  }

  renderSubmit() {
    const { scheduleId } = this.state;

    if (scheduleId) {
      return (
        <div>
          <Button
            variant="outlined"
            color="primary"
            onClick={this.handleCancelEdit}
          >
            Abbrechen
          </Button>
          <Button
            disabled={!this.isSubmitAllowed()}
            variant="contained"
            color="primary"
            onClick={this.handleUpdateEmailReport}
            style={{ marginLeft: '10px' }}
          >
            E-Mail Report aktualisieren
          </Button>
        </div>
      );
    }


    return (
      <Button
        disabled={!this.isSubmitAllowed()}
        variant="contained"
        color="primary"
        onClick={this.handleCreateEmailReport}
      >
        E-Mail Report anlegen
      </Button>
    );
  }

  render() {
    const { open, onCancel, locations } = this.props;
    const { LocationId, ReportId } = this.state;

    const selectedLocation = locations.find(l => l.id === LocationId);
    const selectedReport = selectedLocation && selectedLocation.reports.find(r => r.reportID === ReportId);

    return (
      <Dialog open={open} onClose={onCancel} fullWidth maxWidth="md">
        <DialogTitle style={styles.header} color="primary">E-Mail Reports</DialogTitle>
        <DialogContent>
          <ListSelector
            itemName={selectedLocation ? selectedLocation.name : null}
            label="Lokal"
            itemType="Lokal"
            items={locations.map(loc => ({ value: loc.id, name: loc.name }))}
            onValueChanged={l => this.handleSelectLocation(l.value)}
            hideSearch={locations.length < 5}
          />
          <ReportsFilter
            filterWrapperStyle={{}}
            reports={selectedLocation ? selectedLocation.reports : []}
            selectedReport={selectedReport}
            selectLocation={this.handleSelectLocation}
            selectReport={this.handleChangeReport}
            removeReportData={() => {}}
          />
          {this.renderFrequencySelect()}
          {this.renderSendoutTimeInput()}
          {this.renderPeriodSelect()}
          {this.renderOffsetInput()}
          {this.renderEmailTo()}
          {this.renderParameters()}
          <div style={styles.submitButtonContainer}>
            {this.renderSubmit()}
          </div>
          {this.renderEmailReports()}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} color="primary">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

EmailReportsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  locations: PropTypes.array.isRequired,
  emailReports: PropTypes.array.isRequired,
  selectedLocation: PropTypes.object,
  selectedReport: PropTypes.object,
  parameters: PropTypes.array,
  isLoadingParameters: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  loadEmailReports: PropTypes.func.isRequired,
  createEmailReport: PropTypes.func.isRequired,
  deleteEmailReport: PropTypes.func.isRequired,
  updateEmailReport: PropTypes.func.isRequired,
  loadParams: PropTypes.func.isRequired,
  setEmailParams: PropTypes.func.isRequired,
};

EmailReportsDialog.defaultProps = {
  selectedLocation: null,
  selectedReport: null,
  parameters: null,
};

export default EmailReportsDialog;
